// utils/visitorId.js
// -------------------------------------------------------------
// Returns identifiers for the current browser:
//
// 1) getViewerId()               → stable identifier across sessions
//    • If Firebase Auth has a user (logged-in OR anonymous) → use uid.
//    • Otherwise, store a random UUID in localStorage ("visitorId").
//    • NOTE: This preserves your existing behavior for all features
//      that depend on a long-lived, per-user/per-browser id.
//
// 2) getSessionViewerId()        → session-scoped identifier
//    • In PRODUCTION: scoped to the current browser/incognito *session*,
//      stored ONLY in sessionStorage (per strict session semantics).
//    • In DEVELOPMENT: pinned in localStorage so HMR/fast-refresh / dev
//      reloads won’t generate a new id and make your preview table “vanish”.
//    • Use this for the Display Table so each browser session
//      only sees its own submissions (prod), and stays stable during dev.
// -------------------------------------------------------------

import { getAuth } from 'firebase/auth';
import { v4 as uuidv4 } from 'uuid';

/* ============================================================================
 * 1) Stable viewer id (BACKWARD-COMPATIBLE)
 * ----------------------------------------------------------------------------
 *    This is your original implementation. It remains unchanged so that any
 *    other part of the app relying on a stable id continues to behave the same.
 * ========================================================================== */
export const getViewerId = () => {
  const auth   = getAuth();              // works with the same Firebase app
  const user   = auth.currentUser;

  if (user) return user.uid;             // signed-in or anonymous auth user

  const KEY = 'visitorId';
  let id    = localStorage.getItem(KEY);

  if (!id) {
    id = uuidv4();
    localStorage.setItem(KEY, id);
  }
  return id;
};

/* ============================================================================
 * 2) Session-scoped viewer id
 * ----------------------------------------------------------------------------
 * PRODUCTION:
 *   • A per-session identifier stored in sessionStorage. This ensures that a
 *     brand-new incognito session (or new browser session) starts with a fresh
 *     id and therefore the Display Table shows only *this* session’s rows.
 *
 * DEVELOPMENT:
 *   • Pinned in localStorage so HMR/fast-refresh and dev reloads DO NOT change
 *     the id while you’re testing (prevents “vanishing table” during dev).
 *
 * Usage:
 *   - Use this value for write paths (set `submitterSessionId`) and for read
 *     paths (query `where('submitterSessionId', '==', ...)`) when powering the
 *     Display Table.
 *
 * Behavior:
 *   - Different browser/incognito sessions → different IDs (prod).
 *   - Different tabs within the same session → same ID (prod).
 *   - Dev mode → one pinned id in localStorage to tolerate HMR.
 * ========================================================================== */

/** Legacy session key that might exist from older builds */
const LEGACY_SS_KEY = 'session_viewer_id';

/** New keys (namespaced by env) */
const PROD_SS_SESSION = 'viewer_session_id_v1';
const DEV_LS_SESSION  = 'viewer_session_id_v1_dev';

let _cachedSessionViewerId = null;

/** Small helper: robust id maker with crypto fallback */
function makeId() {
  if (typeof crypto !== 'undefined' && typeof crypto.randomUUID === 'function') {
    return crypto.randomUUID();
  }
  return `${Date.now()}_${Math.random().toString(36).slice(2)}_${uuidv4()}`;
}

/**
 * Returns the session-scoped viewer id.
 * • PRODUCTION → sessionStorage (strict per-session)
 * • DEVELOPMENT → localStorage (stable across HMR/fast-refresh)
 */
export const getSessionViewerId = () => {
  if (_cachedSessionViewerId) return _cachedSessionViewerId;

  const isDev = (typeof process !== 'undefined' && process.env && process.env.NODE_ENV !== 'production');

  /* ─────────────────────────────────────────────────────────────
   * DEVELOPMENT: pin to localStorage so HMR doesn’t change it.
   * Also migrate once from the legacy session key if it exists.
   * ───────────────────────────────────────────────────────────── */
  if (isDev) {
    try {
      let sid = localStorage.getItem(DEV_LS_SESSION);

      if (!sid) {
        // One-time migration: if the old session key exists (from a previous run),
        // use it as the initial dev id so you don’t lose your dev session mid-stream.
        const legacy = (() => {
          try { return sessionStorage.getItem(LEGACY_SS_KEY); } catch { return null; }
        })();

        sid = legacy || makeId();
        localStorage.setItem(DEV_LS_SESSION, sid);
        // eslint-disable-next-line no-console
        console.log('[visitorId] DEV mode: pinned viewer session id in localStorage:', sid);
      }

      _cachedSessionViewerId = sid;
      return sid;
    } catch (e) {
      // eslint-disable-next-line no-console
      console.warn('[visitorId] DEV mode: localStorage unavailable; using ephemeral in-memory session id:', e);
      _cachedSessionViewerId = makeId();
      return _cachedSessionViewerId;
    }
  }

  /* ─────────────────────────────────────────────────────────────
   * PRODUCTION: strict per-session semantics via sessionStorage.
   * Also migrate once from the legacy session key if it exists.
   * ───────────────────────────────────────────────────────────── */
  try {
    let sid = sessionStorage.getItem(PROD_SS_SESSION);

    if (!sid || typeof sid !== 'string') {
      // Try to migrate from the legacy key first
      const legacy = sessionStorage.getItem(LEGACY_SS_KEY);
      sid = legacy || makeId();
      try {
        sessionStorage.setItem(PROD_SS_SESSION, sid);
      } catch (e) {
        // sessionStorage could be blocked/disabled — keep in-memory as a last resort
        // eslint-disable-next-line no-console
        console.warn('[visitorId] PROD mode: sessionStorage set failed; using in-memory session id only:', e);
      }
    }

    _cachedSessionViewerId = sid;
    return sid;
  } catch (e) {
    // sessionStorage unavailable (very rare environments)
    // eslint-disable-next-line no-console
    console.warn('[visitorId] PROD mode: sessionStorage unavailable; using ephemeral in-memory session id:', e);
    _cachedSessionViewerId = makeId();
    return _cachedSessionViewerId;
  }
};
